import { Edit, EmojiEvent, Gift, Lock, PickleBall, Security, SettingsGear, Share, Support } from '@/components/icons'
import { SUPPORT_DIRECT } from '@/constants/Common'
import {
  ROUTE_DASHBOARD,
  ROUTE_PASSWORD_SECURITY,
  ROUTE_PREFERENCES,
  ROUTE_PRIVACY_POLICY,
  ROUTE_PROFILE,
  ROUTE_CONNECTIONS,
  ROUTE_SUBSCRIPTION,
  ROUTE_TERMS,
  ROUTE_MY_BENEFITS,
} from '@/constants/Routes'
import { useCanUseBenefits } from '@/hooks/useCanUseBenefits'
import { useAppSelector } from '@/hooks/useRedux'
import { useMyBenefits } from '@/services/myBenefits'
import { visibleBenefits } from '@/pages/benefits/claimOptions'
import { appSelector } from '@/pages/home/selectors'
import { useState } from 'react'
import { useTranslation } from 'react-i18next'
import { Link, useLocation } from 'react-router-dom'
import { IFrameModal } from '../modal'
import { SubscriptionIcon } from '../icons/SubscriptionIcon'

export const SettingSidebar = () => {
  const { t } = useTranslation()

  const location = useLocation()
  const { isShowMenu } = useAppSelector(appSelector)

  /**
   * How many benefits are sitting unclaimed.
   *
   * The one thing this feature had no passive surface for: the benefits page existed but nothing
   * linked to it, so a member only ever found an unclaimed credit by following a push, a paywall or
   * the banner on a participating club's page. An unclaimed benefit that expires because nobody
   * could find it is exactly what the program is meant to avoid.
   *
   * The query is the same one the benefits page runs, so opening it from here costs nothing.
   */
  const benefitsEnabled = useCanUseBenefits()
  const { data: benefits } = useMyBenefits(benefitsEnabled)
  // Counted as the benefits page lists them: a bundle once, and nothing with nowhere to spend it.
  const unclaimed = visibleBenefits(benefits ?? []).filter((benefit) => benefit.state === 'AVAILABLE').length

  const [openIframe, setOpenIframe] = useState(false)

  const onToggleIframe = () => setOpenIframe(!openIframe)

  return (
    <>
      <div
        className={`${
          isShowMenu ? 'w-60 opacity-100' : 'w-0 opacity-0'
        } transition-all duration-500 ease-in-out hidden lg:flex lg:flex-col fixed lg:gap-y-2`}
      >
        <h2 className="text-2xl text-white font-bold">{t('common.settings')}</h2>
        <div>
          <h3 className="text-white text-opacity-50 font-semibold py-2">{t('common.myAccount')}</h3>
          <Link to={ROUTE_DASHBOARD} className={`flex p-2 rounded-lg items-center`}>
            <PickleBall className={`text-white`} />
            <span className={`ml-4 text-sm font-semibold text-white`}>{t('common.Dashboard')}</span>
          </Link>
          <Link
            to={ROUTE_PROFILE}
            className={`flex p-2 rounded-lg items-center ${
              location.pathname === ROUTE_PROFILE
                ? 'bg-gray-200 bg-opacity-10'
                : 'hover:bg-slate-200 hover:bg-opacity-5 cursor-pointer'
            } `}
          >
            <Edit className={`text-white`} />
            <span className={`ml-4 text-sm font-semibold text-white`}>{t('common.editProfile')}</span>
          </Link>
          <Link
            to={ROUTE_PREFERENCES}
            className={`flex p-2 rounded-lg items-center ${
              location.pathname === ROUTE_PREFERENCES
                ? 'bg-gray-200 bg-opacity-10'
                : 'hover:bg-slate-200 hover:bg-opacity-5 cursor-pointer'
            } `}
          >
            <SettingsGear className={`text-white`} />
            <span className={`ml-4 text-sm font-semibold text-white`}>Preferences</span>
          </Link>
          <Link
            to={ROUTE_CONNECTIONS}
            className={`flex p-2 rounded-lg items-center ${
              location.pathname === ROUTE_CONNECTIONS
                ? 'bg-gray-200 bg-opacity-10'
                : 'hover:bg-slate-200 hover:bg-opacity-5 cursor-pointer'
            } `}
          >
            <Share className={`text-white`} />
            <span className={`ml-4 text-sm font-semibold text-white`}>Connected Apps</span>
          </Link>
          {/* Above Subscriptions, because a benefit is the thing that arrived BECAUSE of one — a
              member looking for what their membership got them reads down, not up. Hidden entirely
              when there is nothing to claim and nothing claimed: an empty row teaches people the
              feature is not for them. */}
          {benefitsEnabled && (benefits ?? []).length > 0 && (
            <Link
              to={ROUTE_MY_BENEFITS}
              className={`flex p-2 rounded-lg items-center ${
                location.pathname.startsWith(ROUTE_MY_BENEFITS)
                  ? 'bg-gray-200 bg-opacity-10'
                  : 'hover:bg-slate-200 hover:bg-opacity-5 cursor-pointer'
              } `}
            >
              <Gift className={`text-white`} />
              <span className={`ml-4 text-sm font-semibold text-white`}>{t('benefits.navTitle')}</span>
              {/* The count, in the mock-up's own pill: green on a translucent green ground, which
                  is legible on this dark sidebar where a solid badge would shout. Only when there
                  is something waiting — a "0" is not news. */}
              {unclaimed > 0 && (
                <span className="ml-auto rounded-[10px] bg-[#00C874]/[0.12] px-[9px] py-0.5 text-[11px] font-semibold text-[#5FDFAE]">
                  {unclaimed}
                </span>
              )}
            </Link>
          )}
          <Link
            to={ROUTE_SUBSCRIPTION}
            className={`flex p-2 rounded-lg items-center ${
              location.pathname === ROUTE_SUBSCRIPTION
                ? 'bg-gray-200 bg-opacity-10'
                : 'hover:bg-slate-200 hover:bg-opacity-5 cursor-pointer'
            } `}
          >
            <SubscriptionIcon className={`text-white`} />
            <span className={`ml-4 text-sm font-semibold text-white`}>{t('common.subscriptions')}</span>
          </Link>
          <Link
            to={ROUTE_PASSWORD_SECURITY}
            className={`flex p-2 rounded-lg items-center ${
              location.pathname.startsWith(ROUTE_PASSWORD_SECURITY)
                ? 'bg-gray-200 bg-opacity-10'
                : 'hover:bg-slate-200 hover:bg-opacity-5 cursor-pointer'
            } `}
          >
            <Lock className={`text-white`} />
            <span className={`ml-4 text-sm font-semibold text-white`}>{t('common.passwordSecurity')}</span>
          </Link>

          <h3 className="text-white text-opacity-50 font-semibold py-2">{t('common.infoSupport')}</h3>
          <Link
            to={SUPPORT_DIRECT}
            className={`flex p-2 rounded-lg items-center hover:bg-slate-200 hover:bg-opacity-5 cursor-pointer`}
            target="_blank"
          >
            <Support className={` text-white`} />
            <span className={`ml-4 text-sm font-semibold text-white `}>{t('common.support')}</span>
          </Link>
          <div
            className={`flex p-2 rounded-lg items-center hover:bg-slate-200 hover:bg-opacity-5 cursor-pointer`}
            onClick={onToggleIframe}
          >
            <EmojiEvent className={`text-white`} />
            <span className={`ml-4 text-sm font-semibold text-white `}>{t('common.runEvents')}</span>
          </div>
          <Link
            to={'https://www.dupr.com/community-guidelines'}
            className={`flex p-2 rounded-lg items-center hover:bg-slate-200 hover:bg-opacity-5 cursor-pointer`}
            target="_blank"
          >
            <Security className={`text-white`} />
            <span className={`ml-4 text-sm font-semibold text-white `}>{t('common.communityGuidelines')}</span>
          </Link>
          <Link
            to={ROUTE_TERMS}
            className={`flex p-2 rounded-lg items-center hover:bg-slate-200 hover:bg-opacity-5 cursor-pointer`}
          >
            <Security className={`text-white`} />
            <span className={`ml-4 text-sm font-semibold text-white `}>{t('common.termsUse')}</span>
          </Link>
          <Link
            to={ROUTE_PRIVACY_POLICY}
            className={`flex p-2 rounded-lg items-center hover:bg-slate-200 hover:bg-opacity-5 cursor-pointer`}
          >
            <Security className={`text-white`} />
            <span className={`ml-4 text-sm font-semibold text-white `}>{t('common.privacyPolicy')}</span>
          </Link>
        </div>
      </div>
      <IFrameModal open={openIframe} onClose={onToggleIframe} />
    </>
  )
}
